import { Button } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';
import { FC, useEffect, useState } from 'react';
import ProgressDetailedSkillReportInfoModal from './ProgressModals/ProgressDetailedSkillReportInfoModal';
import { ProgressStudentSelector } from './ProgressStudentTablesContainer';
import { ProgressStudentsRoadmapByGradeQuery$variables } from '../../graphQLOperations/__generated__/ProgressStudentsRoadmapByGradeQuery.graphql';
import { useAppSelector } from '../../redux/hooks';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { ProgressStudentsGrowthAndEngagementQuery$variables } from '../../graphQLOperations/__generated__/ProgressStudentsGrowthAndEngagementQuery.graphql';
import ProgressDetailedSkillReportContainer from './ProgressModals/ProgressDetailedSkillReportContainer';
import { selectedCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { useLazyLoadQuery } from 'react-relay';
import { getAllTargetSkillTypes } from '../../graphQLOperations/Skills';
import { SkillsAllTargetSkillTypesQuery } from '../../graphQLOperations/__generated__/SkillsAllTargetSkillTypesQuery.graphql';
import { useGetDropdownsOptionForReports } from '../../custom-hooks/useGetDropdownsOptionForReports';
import { selectUser } from '../../redux/reducers/userSlice';

interface Props {
  headerSelector: ProgressStudentSelector;
  tracked?: boolean;
  setHeaderSelector: (headerSelector: ProgressStudentSelector) => void;
  loadRoadmapQuery: (r: ProgressStudentsRoadmapByGradeQuery$variables) => void;
  loadGrowthQuery: (g: ProgressStudentsGrowthAndEngagementQuery$variables) => void;
}

const ProgressStudentTablesHeader: FC<Props> = ({
  headerSelector,
  tracked = true,
  setHeaderSelector,
  loadRoadmapQuery,
  loadGrowthQuery,
}) => {
  const { allDropdowns, setAllDropdowns, allClassesData } = useGetDropdownsOptionForReports();
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  const userRedux = useAppSelector(selectUser);

  const menuItems = [
    {
      id: tracked ? 'progressStudentsSnapshot' : undefined,
      label: 'Snapshot',
      template: (item: MenuItem) => itemRenderer(item, 0),
    },
    {
      id: tracked ? 'progressStudentsGE' : undefined,
      label: 'Growth & Engagement',
      template: (item: MenuItem) => itemRenderer(item, 1),
    },
    {
      id: tracked ? 'progressStudentsRMProgressbyGrade' : undefined,
      label: 'Roadmap Progress by Grade',
      template: (item: MenuItem) => itemRenderer(item, 2),
    },
  ];

  const { allTargetSkillTypes } = useLazyLoadQuery<SkillsAllTargetSkillTypesQuery>(
    getAllTargetSkillTypes,
    {},
    { fetchPolicy: 'network-only' },
  );

  const handleSwitch = (itemIndex: number) => {
    setHeaderSelector(itemIndex);
    if (itemIndex === 1) {
      loadGrowthQuery({
        filter: {
          targetGroupId: reportsFilterRedux.roadmaps.id,
          classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
          skillTypeIds: allTargetSkillTypes?.filter(type => type?.name !== 'Extension')?.map(type => type?.id),
        },
      });
    }
    if (itemIndex === 2) {
      loadRoadmapQuery({
        filter: {
          targetGroupId: reportsFilterRedux.roadmaps.id,
          classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
          standardStateId:
            userRedux?.sites?.find(el => el.id === reportsFilterRedux.sites.id)?.standardState?.id ??
            'StandardStateNode:1',
        },
      });
    }
  };

  const handleDownload = () => {
    const notShowAgain = sessionStorage.getItem('not_show_again_detailed_skill_report_info');
    if (notShowAgain === 'true') setDownloadModalVisible(true);
    else setInfoModalVisible(true);
  };

  const itemRenderer = (item: MenuItem, itemIndex: number) => (
    <a className="px-1 m-1" onClick={() => handleSwitch(itemIndex)}>
      <span className="font-medium">{item.label}</span>
    </a>
  );

  useEffect(() => {
    if (headerSelector === ProgressStudentSelector.ROADMAP_PROGRESS_BY_GRADE) {
      loadRoadmapQuery({
        filter: {
          targetGroupId: reportsFilterRedux.roadmaps.id,
          classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
          standardStateId:
            userRedux?.sites?.find(el => el.id === reportsFilterRedux.sites.id)?.standardState?.id ??
            'StandardStateNode:1',
        },
      });
    }

    if (headerSelector === ProgressStudentSelector.GROWTH_AND_ENGAGEMENT) {
      loadGrowthQuery({
        filter: {
          targetGroupId: reportsFilterRedux.roadmaps.id,
          classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
          skillTypeIds: allTargetSkillTypes?.filter(type => type?.name !== 'Extension')?.map(type => type?.id),
        },
      });
    }
  }, [reportsFilterRedux, headerSelector]);

  return (
    <>
      <div className="flex flex-column justify-content-between align-items-center pb-3 gap-3 lg:gap-0 lg:flex-row">
        <div className="flex border-1 border-round-lg" style={{ borderColor: '#1259FB' }}>
          <TabMenu
            activeIndex={headerSelector}
            onTabChange={e => setHeaderSelector(e.index)}
            className="nc_tab-button-group"
            model={menuItems}
          />
        </div>
        <Button
          label="Download Detailed Skill Report"
          icon="nc_icon-nc_export icon-sm"
          rounded
          outlined
          onClick={handleDownload}
          iconPos="right"
          disabled={isCsvProcessing}
          id="progressDLDetailedSkillReport"
        />
      </div>
      {infoModalVisible && (
        <ProgressDetailedSkillReportInfoModal
          visible={infoModalVisible}
          setVisible={setInfoModalVisible}
          setDownloadModalVisible={setDownloadModalVisible}
          downloadModalVisible={downloadModalVisible}
          allClassesData={allClassesData}
          allDropdowns={allDropdowns}
          setAllDropdowns={setAllDropdowns}
        />
      )}
      {downloadModalVisible && (
        <ProgressDetailedSkillReportContainer
          visible={downloadModalVisible}
          setVisible={setDownloadModalVisible}
          allClassesData={allClassesData}
          allDropdowns={allDropdowns}
          setAllDropdowns={setAllDropdowns}
        />
      )}
    </>
  );
};

export default ProgressStudentTablesHeader;
